import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { TextField } from '@/shared/ui/TextField';
import {
  RAFFLE_STATUS,
  fetchRaffleInfo,
  fetchRaffleStatuses,
  approveRaffleResult,
  playRafflePrize,
  publishRaffle,
  type RaffleInfo,
  type RafflePrize,
  type RaffleResult,
  type RaffleStatusInfo,
} from './api';

const NAME_MIN_LENGTH = 3;
const CITY_MIN_LENGTH = 4;
const REQUIRED = 'Это поле обязательно для заполнения';
const INVALID = 'Некорректное значение';

const validate = (value: string, minLength: number) => {
  const trimmed = value.trim();
  if (!trimmed) return REQUIRED;
  return trimmed.length < minLength ? INVALID : undefined;
};

type ModerationProps = {
  result: RaffleResult;
  onClose: () => void;
  onSubmit: (values: { userName: string; city: string }) => void;
  isSaving: boolean;
};

const ModerationDialog = ({ result, onClose, onSubmit, isSaving }: ModerationProps) => {
  const [userName, setUserName] = useState(result.user.name ?? '');
  const [city, setCity] = useState(result.user.city_name ?? '');
  const [touched, setTouched] = useState(false);

  const nameError = validate(userName, NAME_MIN_LENGTH);
  const cityError = validate(city, CITY_MIN_LENGTH);

  const submit = () => {
    setTouched(true);
    if (!nameError && !cityError) onSubmit({ userName: userName.trim(), city: city.trim() });
  };

  return (
    <ModalDialog
      title="Подтверждение победителя"
      onClose={onClose}
      onSave={submit}
      isSaving={isSaving}
    >
      <TextField
        label="ФИО победителя"
        placeholder="Введите имя победителя"
        value={userName}
        errors={touched && nameError ? [nameError] : undefined}
        onChange={setUserName}
      />
      <TextField
        label="Город победителя"
        placeholder="Введите название города победителя"
        value={city}
        errors={touched && cityError ? [cityError] : undefined}
        onChange={setCity}
      />
    </ModalDialog>
  );
};

type CardProps = { prize: RafflePrize; result: RaffleResult; onClose: () => void };

const WinnerCard = ({ prize, result, onClose }: CardProps) => {
  const { user } = result;
  const rows: [string, string | number | undefined][] = [
    ['ФИО', user.name || result.user_name],
    ['Город', user.city_name],
    ['Телефон', user.phone],
    ['Номер купона', result.coupon_number],
    ['ID', user.id],
  ];
  return (
    <ModalDialog title="Карточка победителя" onClose={onClose}>
      <div style={{ textAlign: 'center', marginBottom: 15 }}>
        {prize.image?.thumb_140_140 && <img src={prize.image.thumb_140_140} alt="" />}
        <div>
          <small>Приз</small>
          <div>
            <strong>{prize.name}</strong>
          </div>
        </div>
      </div>
      {rows.map(([label, value]) => (
        <div className="form-group" key={label}>
          <label className="control-label">{label}</label>
          <input className="form-control input-sm" value={value ?? ''} disabled />
        </div>
      ))}
    </ModalDialog>
  );
};

type PrizeRowProps = {
  prize: RafflePrize;
  raffleId: number;
  status?: RaffleStatusInfo;
  setRaffle: (raffle: RaffleInfo) => void;
};

const PrizeRow = ({ prize, raffleId, status, setRaffle }: PrizeRowProps) => {
  const [dialog, setDialog] = useState<'approve' | 'card' | null>(null);
  const { result } = prize;

  const play = useMutation({
    mutationFn: () => playRafflePrize(prize.id),
    onSuccess: (raffle) => raffle && setRaffle(raffle),
    onError: () => window.alert('Не удалось разыграть приз'),
  });
  const moderate = useMutation({
    mutationFn: async (v: { userName: string; city: string }) => {
      if (!result) return;
      await approveRaffleResult(result.id, v);
      setRaffle(await fetchRaffleInfo(raffleId));
    },
    onSuccess: () => setDialog(null),
    onError: () => window.alert('Не удалось сохранить результат'),
  });

  return (
    <tr>
      <td>
        {prize.image?.thumb_140_140 && <img src={prize.image.thumb_140_140} alt="" />}
        <div>{prize.name}</div>
        {prize.is_main && <strong>Главный приз</strong>}
        {!result && (
          <button
            type="button"
            className="btn btn-info btn-sm"
            disabled={play.isPending || (status ? !status.can_played : false)}
            onClick={() => play.mutate()}
          >
            Разыграть приз
          </button>
        )}
      </td>
      <td>{result?.coupon_number}</td>
      <td>{result?.user_name}</td>
      <td>{result?.user.id}</td>
      <td>{result?.user.phone}</td>
      <td>{result?.user.city_name}</td>
      <td>
        {result && (
          <>
            {result.approved ? (
              <p>Победитель выбран</p>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  style={{ marginRight: 6 }}
                  onClick={() => setDialog('approve')}
                >
                  Подтвердить победителя
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  style={{ marginRight: 6 }}
                  disabled={play.isPending}
                  onClick={() => play.mutate()}
                >
                  Переиграть
                </button>
              </>
            )}
            <button type="button" className="btn btn-default btn-sm" onClick={() => setDialog('card')}>
              Карточка победителя
            </button>
            {dialog === 'approve' && (
              <ModerationDialog
                result={result}
                isSaving={moderate.isPending}
                onClose={() => setDialog(null)}
                onSubmit={(values) => moderate.mutate(values)}
              />
            )}
            {dialog === 'card' && <WinnerCard prize={prize} result={result} onClose={() => setDialog(null)} />}
          </>
        )}
      </td>
    </tr>
  );
};

export const RaffleViewPage = () => {
  const id = useRouteId();
  const qc = useQueryClient();
  const infoKey = ['raffle-info', id];
  const info = useQuery({ queryKey: infoKey, queryFn: () => fetchRaffleInfo(id) });
  const statuses = useQuery({ queryKey: ['raffle-statuses'], queryFn: fetchRaffleStatuses });

  const raffle = info.data;
  const status = statuses.data?.find((item) => item.id === raffle?.status_id);
  const setRaffle = (next: RaffleInfo) => qc.setQueryData(infoKey, next);

  const publish = useMutation({
    mutationFn: () => publishRaffle(id),
    onSuccess: (next) => next && setRaffle(next),
    onError: () => window.alert('Не удалось опубликовать розыгрыш'),
  });

  const allApproved = raffle?.prizes.every((prize) => prize.result?.approved) ?? false;
  const canPublish = allApproved && raffle?.status_id !== RAFFLE_STATUS.CLOSED;

  return (
    <DetailLayout title="Розыгрыши" isLoading={info.isLoading} error={info.error} errorText="Ошибка загрузки розыгрыша">
      <p>
        <Link to="/raffle">⇐ Вернуться назад</Link>
      </p>
      {raffle && (
        <>
          <h3 className="box-title">
            {raffle.name}{' '}
            {status && (
              <span className="label" style={{ background: status.color, color: '#fff' }}>
                {status.name}
              </span>
            )}{' '}
            {raffle.status_id !== RAFFLE_STATUS.ACTIVE && (
              <button
                type="button"
                className="btn btn-success"
                disabled={!canPublish || publish.isPending}
                onClick={() => publish.mutate()}
              >
                Опубликовать
              </button>
            )}
          </h3>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Приз</th>
                <th>Номер купона</th>
                <th>Победитель</th>
                <th>ID</th>
                <th>Телефон</th>
                <th>Город</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {raffle.prizes.map((prize) => (
                <PrizeRow key={prize.id} prize={prize} raffleId={raffle.id} status={status} setRaffle={setRaffle} />
              ))}
            </tbody>
          </table>
        </>
      )}
    </DetailLayout>
  );
};
